<script lang="ts">
	//UI
	import { DefaultLoading, PopperIcon } from "@hitit/new_ui";
	import AddPaymentModal from "./AddPaymentModal.svelte";
	import CashReceiptModal from "./CashReceiptModal.svelte";
	import EmailReceiptModal from "./EmailReceiptModal.svelte";
	import TransactionLogging from "./TransactionLogging.svelte";
	import AreYouSure from "@hitit/hub/src/v7/components/common/AreYouSure.svelte";

	//Image
	import CASH_CANCELED from "@hitit/hub/src/assets/Icons/order_cancel_cash.png";
	import CARD_CANCELED from "@hitit/hub/src/assets/Icons/order_cancel_card.png";
	import CARD from "@hitit/hub/src/assets/Icons/order_card.png";
	import CASH from "@hitit/hub/src/assets/Icons/order_cash.png";
	import UNKNOWN from "@hitit/hub/src/assets/Icons/order_unknown.png";
	import plusBtn from "@hitit/hub/src/assets/Icons/plusBtn.png";

	//Utils
	import { createEventDispatcher, onMount } from "svelte";
	import { t, api, notifications } from "@hitit/hub/src/stores";
	import { formatNumber } from "@hitit/utils";
	import { popup } from "@hitit/hub/src/v7/store";
	import { getDateDiff } from "@hitit/hub/src/v7/utils";

	//Type
	import type {
		CashReceiptResponse,
		OrderData,
		PaymentData,
		PaymentTransactionData,
	} from "@hitit/api";

	interface MergePaymentDatas extends PaymentData {
		cashReceiptResponse?: CashReceiptResponse;
	}

	export let orderId: string = "";
	export let orderUnwrapDTO: OrderData;

	const dispatch = createEventDispatcher();

	let paymentDatas: MergePaymentDatas[] = [];
	let addPaymentModal = false;
	let selectedPayment: MergePaymentDatas;
	let div: HTMLDivElement;
	let divHeight = 0;
	let cashReceiptModal = false;
	let cashReceiptModalData: MergePaymentDatas;
	let emailReceiptModal = false;
	let hover = false;
	let DESCRIPTION1 = "현금영수증 발급은 결제일로부터 5일 이내로 가능합니다";
	let DESCRIPTION2 = "더이상 수정이 불가능 합니다";
	let loading = false;
	let mountLoading = false;
	let clickType = "";
	let transactionModalStep = 2;

	function getPaymentImage(paymentMethod: string, paymentStatus: string) {
		if (paymentMethod === "INSTORE_CASH" && paymentStatus === "PAID") {
			return CASH;
		} else if (
			paymentMethod === "INSTORE_CASH" &&
			paymentStatus === "REFUNDED"
		) {
			return CASH_CANCELED;
		} else if (paymentMethod === "INSTORE_CAT" && paymentStatus === "PAID") {
			return CARD;
		} else if (
			paymentMethod === "INSTORE_CAT" &&
			paymentStatus === "REFUNDED"
		) {
			return CARD_CANCELED;
		} else {
			return UNKNOWN;
		}
	}

	function getPaymentText(paymentMethod: string, paymentStatus: string) {
		if (paymentStatus === "NOTPAID") {
			return "NOTPAID";
		} else if (paymentMethod === "INSTORE_CASH") {
			return "cash";
		} else if (paymentMethod === "INSTORE_CAT") {
			return "card";
		}
	}

	function omittedPaymentBtnHandler(paymentData: PaymentData) {
		let condition1 = false;
		let condition2 = false;

		if (paymentData.paymentTransactionDTOs?.length === 2) {
			condition1 = paymentData.paymentTransactionDTOs.some(
				(v) => v.paymentTransactionType === "REQUEST_PAY" && v.success === true
			);
			condition2 = paymentData.paymentTransactionDTOs.some(
				(v) =>
					v.paymentTransactionType === "REQUEST_REFUND" && v.success === true
			);
		} else {
			return false;
		}

		return condition1 && condition2;
	}

	function modalOpenStyle() {
		document.body.style.overflow = "hidden";
		document.body.style.touchAction = "none";
	}

	function modalCloseStyle() {
		document.body.style.overflow = "unset";
		document.body.style.touchAction = "unset";
	}

	function addPaymentModalHandler(
		payment: MergePaymentDatas | undefined,
		type: string
	) {
		if (payment) {
			selectedPayment = payment;
		}

		clickType = type;
		clickType === "paymentAdd"
			? (transactionModalStep = 1)
			: (transactionModalStep = 2);
		addPaymentModal = true;
		modalOpenStyle();
	}

	function addPaymentModalCloseHandler() {
		addPaymentModal = false;
		modalCloseStyle();
	}

	function cashReceiptModalOpen(paymentData: MergePaymentDatas) {
		cashReceiptModal = true;
		modalOpenStyle();
		cashReceiptModalData = paymentData;
	}

	function emailReceiptModalOpen(paymentData: MergePaymentDatas) {
		emailReceiptModal = true;
		modalOpenStyle();
		cashReceiptModalData = paymentData;
	}

	function paymentCancel() {
		dispatch("PAYMENT_CANCEL", { orderStatus: "CLOSED" });
	}

	async function paymentCancelHandler(
		data: PaymentTransactionData & { clickType: string }
	) {
		if (!selectedPayment.id) throw new Error("paymentId is undefined");

		const { clickType, ...paymentTransaction } = data;

		// 카드 결제 취소
		if (selectedPayment.paymentMethod === "INSTORE_CAT") {
			try {
				delete paymentTransaction.createdDate;
				delete paymentTransaction.catResponseDTO?.log;
				const merge = { ...paymentTransaction, paymentId: selectedPayment.id };
				await $api.postTransactions(selectedPayment.id, merge);
				paymentCancel();
				addPaymentModalCloseHandler();
				loadPayments(orderId);
			} catch (e) {
				const error = e as Error;
				const errorMessage = `
				ERROR STATUS : ${error.message},<br>
				다시 한번 확인 바랍니다.
				`;
				notifications.append("ERROR", errorMessage, "", undefined, "ERROR");
				console.log(e);
			}
		}
		// 현금 결제 취소
		if (
			selectedPayment.paymentMethod === "INSTORE_CASH" &&
			selectedPayment.cashReceiptStatus === "CASH_RECEIPT_ISSUED"
		) {
			try {
				const cancelCashReceiptDTO = {
					cashreceiptId: selectedPayment.cashReceiptResponse?.id || "",
					cancelType: 0,
				};
				const r = await $api.postCancelCashReceipt(cancelCashReceiptDTO);
				const { cashReceiptTransactionCancel } = r.data;
				const dataId = r.data.id;
				const uuid = cashReceiptTransactionCancel.id;
				const {
					mclientVersion,
					mdeviceType,
					mdeviceUUID,
					createdDate,
					id,
					...flatPaymentTransactionDTO
				} = selectedPayment.paymentTransactionDTOs![0];

				delete flatPaymentTransactionDTO.catResponseDTO!.log;

				flatPaymentTransactionDTO.paymentTransactionType = "REQUEST_REFUND";
				flatPaymentTransactionDTO.catResponseDTO!.acquirerName = "";
				flatPaymentTransactionDTO.catResponseDTO!.responseMessage2 = "";
				flatPaymentTransactionDTO.catResponseDTO!.storeCode = "";
				flatPaymentTransactionDTO.catResponseDTO!.terminalId = "web";
				flatPaymentTransactionDTO.catResponseDTO!.authorizationID = dataId;
				flatPaymentTransactionDTO.catResponseDTO!.transactionUUID = uuid;
				flatPaymentTransactionDTO.catResponseDTO!.destinationIp = "";
				flatPaymentTransactionDTO.catResponseDTO!.sourceIp = "";
				flatPaymentTransactionDTO.catResponseDTO!.transactionType = "CANCEL";
				flatPaymentTransactionDTO.catResponseDTO!.catResponseType =
					"CASH_CANCEL_RECEIPT";

				await $api.postTransactions(
					selectedPayment.paymentTransactionDTOs![0].paymentId,
					flatPaymentTransactionDTO
				);
				paymentCancel();
				addPaymentModalCloseHandler();
				loadPayments(orderId);
			} catch (e) {
				const error = e as Error;
				const errorMessage = `
				ERROR STATUS : ${error.message},<br>
				다시 한번 확인 바랍니다.
				`;
				notifications.append("ERROR", errorMessage, "", undefined, "ERROR");
				console.log(e);
			}
		} else if (
			selectedPayment.paymentMethod === "INSTORE_CASH" &&
			selectedPayment.cashReceiptStatus !== "CASH_RECEIPT_ISSUED"
		) {
			try {
				delete paymentTransaction.createdDate;
				delete paymentTransaction.catResponseDTO;

				const merge = { ...paymentTransaction, paymentId: selectedPayment.id };
				await $api.postTransactions(selectedPayment.id, merge);
				paymentCancel();
				addPaymentModalCloseHandler();
				loadPayments(orderId);
			} catch (e) {
				const error = e as Error;
				const errorMessage = `
				ERROR STATUS : ${error.message},<br>
				다시 한번 확인 바랍니다.
				`;
				notifications.append("ERROR", errorMessage, "", undefined, "ERROR");
				console.log(e);
			}
		}
	}

	async function paymentAddHandler() {}

	async function paymentDataHandler(
		data: PaymentTransactionData & { clickType: string }
	) {
		const { clickType, ...paymentTransaction } = data;
		if (clickType === "paymentCancel") {
			$popup = {
				component: AreYouSure,
				props: {
					textTranslation: `대표님의 걱정어린 재확인 &#58; </br> 해당 결제를 '정말로' 취소로 변경하시겠습니까?`,
					accept: () => {
						paymentCancelHandler(data);
						$popup = undefined;
					},
					cancel: () => ($popup = undefined),
					imageSrc: "/img/icons/bkWarning.png",
				},
			};
		} else if (clickType === "paymentTransactionAdd") {
			// console.log("paymentTransactionAdd");
			// console.log("data", data);
			// console.log("selectedPayment", selectedPayment);
			const newPaymentId = selectedPayment?.id || "";
			const merge = { ...paymentTransaction, paymentId: newPaymentId };
			await $api.postTransactions(selectedPayment?.id || "", merge);
			addPaymentModalCloseHandler();
			loadPayments(orderId);
		} else if (clickType === "paymentAdd") {
			try {
				const r = await $api.postPayment(
					"INSTORE_CAT",
					orderUnwrapDTO.storeid || "",
					orderUnwrapDTO.id || ""
				);
				const newPaymentId = r.data.id;
				const merge = { ...data, paymentId: newPaymentId };
				await $api.postTransactions(newPaymentId, merge);
				addPaymentModalCloseHandler();
				loadPayments(orderId);
			} catch (e) {
				const error = e as Error;
				const errorMessage = `
				ERROR STATUS : ${error.message},<br>
				다시 한번 확인 바랍니다.
				`;
				notifications.append("ERROR", errorMessage, "", undefined, "ERROR");
				console.log(e);
			}
		}
	}

	async function loadPayments(orderId: string) {
		const r = (await $api.listOrderPayments(orderId)).data.paymentDTOs;

		let sortedPayment = r.sort(
			(a, b) =>
				new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
		);
		sortedPayment.forEach((v) => {
			if (v.paymentTransactionDTOs?.length !== 0) {
				v.paymentTransactionDTOs = v.paymentTransactionDTOs?.sort(
					(a, b) =>
						new Date(b.createdDate || "").getTime() -
						new Date(a.createdDate || "").getTime()
				);
			}
		});

		for (let [index, item] of sortedPayment.entries()) {
			if (
				item.paymentMethod === "INSTORE_CASH" &&
				item.paymentTransactionDTOs &&
				item.paymentTransactionDTOs[0]?.catResponseDTO?.authorizationID
			) {
				const cashResponse = await $api.getCashReceipt(
					item.paymentTransactionDTOs[0]?.catResponseDTO?.authorizationID
				);

				sortedPayment[index] = {
					...sortedPayment[index],
					cashReceiptResponse: cashResponse.data,
				};
			} else {
				sortedPayment[index] = {
					...sortedPayment[index],
					cashReceiptResponse: undefined,
				};
			}
		}

		paymentDatas = sortedPayment;
	}

	function setDateStandard(paymentData: MergePaymentDatas) {
		let standard = "";
		if (paymentData && paymentData.cashReceiptResponse) {
			standard =
				getDateDiff(
					paymentData?.cashReceiptResponse?.cashReceiptTransactionIssue
						?.tradeDate || ""
				) || "";
		}
		return standard;
	}

	function cashReceiptResponseCheck(paymentData: MergePaymentDatas) {
		// 발행 취소 둘다 존재하면 1사이클 돈것임
		let check = false;
		if (paymentData && paymentData.cashReceiptResponse) {
			check =
				paymentData.cashReceiptResponse.cashReceiptTransactionIssue !== null &&
				paymentData.cashReceiptResponse.cashReceiptTransactionCancel !== null;
		}
		return check;
	}

	onMount(async () => {
		mountLoading = true;
		await loadPayments(orderId);
		if (div) {
			divHeight = div.clientHeight;
			let childNode = div.children[0] as HTMLDivElement;
			childNode.style.height = `${divHeight - 10}px`;
		}
		mountLoading = false;
	});

	$: paymentDatas;
</script>

{#if addPaymentModal}
	<AddPaymentModal
		addPaymentModalCloseHandler={addPaymentModalCloseHandler}
		paymentTransactionDto={undefined}
		selectedPaymentMethod={clickType === "paymentAdd"
			? undefined
			: selectedPayment.paymentMethod}
		step={transactionModalStep}
		clickType={clickType}
		loading={loading}
		on:ADD_CARD_PAYMENT={(e) => paymentDataHandler(e.detail)}
	/>
{/if}
{#if cashReceiptModal}
	<CashReceiptModal
		bind:cashReceiptModal={cashReceiptModal}
		cashReceiptModalData={cashReceiptModalData}
		loadPayments={() => loadPayments(orderId)}
	/>
{/if}
{#if emailReceiptModal}
	<EmailReceiptModal
		bind:emailReceiptModal={emailReceiptModal}
		emailReceiptModalData={cashReceiptModalData}
		loadPayments={() => loadPayments(orderId)}
	/>
{/if}
{#if mountLoading}
	<div class="loading">
		<DefaultLoading />
	</div>
{/if}
{#if paymentDatas.length !== 0}
	{#each paymentDatas as paymentData}
		<div class="container card" t-id="PaymentTab">
			<div class="payment-container">
				<div class="payment-wrapper">
					<div class="payment-box">
						<div class="img-box">
							<img
								src={getPaymentImage(
									paymentData.paymentMethod,
									paymentData.paymentStatus || ""
								)}
								alt=""
							/>
							<span>
								{$t(
									`hub.order.payment_type.${getPaymentText(
										paymentData.paymentMethod,
										paymentData.paymentStatus || ""
									)}`
								)}
							</span>
						</div>
						{#if paymentData.paymentMethod === "INSTORE_CAT"}
							<div class="card-info">
								{#if paymentData.paymentTransactionDTOs && paymentData.paymentTransactionDTOs.length !== 0}
									<span>
										{`${
											paymentData.paymentTransactionDTOs[0]?.catResponseDTO
												?.acquirerName || ""
										} ${
											paymentData.paymentTransactionDTOs[0]?.catResponseDTO
												?.maskedCardNumber || ""
										}`}
									</span>
								{/if}
							</div>
						{/if}
					</div>

					<div class="amount">
						{#if paymentData.paymentTransactionDTOs && paymentData.paymentTransactionDTOs?.length !== 0}
							<span>
								{formatNumber(
									paymentData.paymentTransactionDTOs[0]?.totalReceivedPrice
								)}{$t("global.KRW")}
							</span>
						{/if}
					</div>
				</div>
				<div class="payment-widget" bind:this={div}>
					<div class="vertical-bar" />
					<div class="widget-group">
						<div class="top-widgets">
							<!-- payment가 미결제일때 결제 취소로 변경 및 이메일 영수증 이런거 전부 비활성화 해야됨 -->
							{#if paymentData.paymentTransactionDTOs && paymentData.paymentTransactionDTOs[0]?.paymentTransactionType !== "REQUEST_REFUND"}
								<!-- <div
									class="payment-cancel-widget"
									on:click={() =>
										addPaymentModalHandler(paymentData, "paymentCancel")}
								>
									<span>{$t("hub.order.change_payment_cancel")}</span>
								</div> -->
							{/if}
							<!--  cancelHandler는 잘동작함. 다만 앱의 경우 현금영수증 취소 후 결제취소까지 자동으로 취소됨(결국 앱은 현금영수증 단독취소가 없다는뜻) 대표님께서 이 기능은 웹에서 굳이 있어야할 필요가 없다고 하심 26.07.21  -->
							<!-- {#if paymentData.cashReceiptStatus !== "NA" && paymentData.paymentStatus !== "REFUNDED"}
								{#if setDateStandard(paymentData) === "moreThanFive" || (setDateStandard(paymentData) === "betweenOneAndFive" && cashReceiptResponseCheck(paymentData))}
									<div
										class="cash-receipt-widget"
										class:moreThanFive={setDateStandard(paymentData) ===
											"moreThanFive"}
										class:betweenOneAndFive={setDateStandard(paymentData) ===
											"betweenOneAndFive"}
										on:mouseenter={() => (hover = true)}
										on:mouseleave={() => (hover = false)}
									>
										<span
											>{paymentData.cashReceiptStatus === "UNKNOWN" ||
											paymentData.cashReceiptStatus === "CASH_RECEIPT_CANCELED"
												? "현금영수증 발행"
												: "현금영수증 보기"}</span
										>
										{#if hover}
											<div class="description">
												<div class="icon">
													<PopperIcon fill={"#6d6f76"} />
												</div>
												{#if setDateStandard(paymentData) === "moreThanFive"}
													{@html DESCRIPTION1}
												{:else if setDateStandard(paymentData) === "betweenOneAndFive"}
													{@html DESCRIPTION2}
												{/if}
											</div>
										{/if}
									</div>
								{:else}
									<div
										class="cash-receipt-widget"
										on:click={() => cashReceiptModalOpen(paymentData)}
									>
										<span
											>{paymentData.cashReceiptStatus === "UNKNOWN" ||
											paymentData.cashReceiptStatus === "CASH_RECEIPT_CANCELED"
												? "현금영수증 발행"
												: "현금영수증 보기"}</span
										>
									</div>
								{/if}
							{/if} -->
							{#if !paymentData.receiptEmailRequested && paymentData.paymentStatus !== "NOTPAID"}
								<div
									class="email-receipt-widget"
									on:click={() => emailReceiptModalOpen(paymentData)}
								>
									<span>{$t("hub.order.email_receipt.email_receipt")}</span>
								</div>
							{/if}
						</div>
						{#if omittedPaymentBtnHandler(paymentData) === false}
							<!-- 트랜잭션 추가 임시 비활성화 -->
							<!-- <div
								class="payment-transaction-add-box"
								on:click={() =>
									addPaymentModalHandler(paymentData, "paymentTransactionAdd")}
							>
								<img src={plusBtn} alt="plus" /><span
									>{$t("hub.order.add_payment_transaction")}</span
								>
							</div> -->
						{/if}
					</div>
				</div>
			</div>
			<div class="transaction-wrapper">
				{#each paymentData.paymentTransactionDTOs || [] as transaction}
					<TransactionLogging transactionData={transaction} />
				{/each}
			</div>
		</div>
	{/each}
{:else}
	<div class="not-data-container">
		<div class="icon">
			<PopperIcon fill={"#46474b"} />
		</div>
		<span>{$t("hub.order.no_payment_information")}</span>
	</div>
{/if}

<!-- 결제추가는 트랜잭션 추가가 선행완료 후 작업 -->
<!-- <div
	class="payment-add-box"
	on:click={() => addPaymentModalHandler(undefined, "paymentAdd")}
>
	<img src={plusBtn} alt="plus" /><span>{$t("hub.order.add_payment")}</span>
</div> -->

<style lang="scss">
	@import "../../../variables.scss";

	.loading {
		width: 30px;
		height: 30px;
		display: flex;
		justify-content: center;
		align-items: center;
		margin: 0 auto;
	}

	.card {
		padding: 15px 20px;
		background-color: white;
		border-radius: 5px;
	}

	.container {
		display: flex;
		flex-direction: column;
		gap: 15px;
		.payment-container {
			display: flex;
			flex-direction: column;
			gap: 10px;
			/* justify-content: space-between;
			align-items: flex-start; */

			.payment-wrapper {
				display: flex;
				justify-content: space-between;
				align-items: center;
				max-width: 1000px;
				width: 100%;
				.payment-box {
					display: flex;
					flex-direction: column;
					justify-content: flex-start;
					width: 100%;
					gap: 10px;
					.img-box {
						display: flex;
						align-items: center;
						gap: 10px;

						span {
							@include appleAdjustTextForSize(16);
							font-weight: 550;
							color: #46474b;
						}
						img {
							width: 30px;
							height: 22px;
						}
					}
					.card-info {
						width: 100%;
						span {
							@include appleAdjustTextForSize(14, false);
							@include heightRelativeToFontSize(16);
							color: #46474b;
						}
					}
				}
				.amount {
					span {
						@include appleAdjustTextForSize(16);
						font-weight: 550;
						color: #46474b;
					}
				}
			}

			.vertical-bar {
				display: none;
			}

			.payment-widget {
				display: flex;
				align-items: center;
				width: 100%;

				.widget-group {
					display: flex;
					flex-direction: column;
					width: 100%;
					gap: 10px;
					.top-widgets {
						display: flex;
						flex-direction: column;
						gap: 10px;
					}

					.payment-transaction-add-box {
						display: flex;
						align-items: center;
						justify-content: center;
						height: 30px;
						border-radius: 20px;
						background-color: #e6e7ed;
						gap: 10px;
						cursor: pointer;

						span {
							@include appleAdjustTextForSize(16);
							font-weight: 400;
							color: #46474b;
						}

						img {
							width: 16px;
							height: 16px;
						}
					}
				}

				.cash-receipt-widget {
					position: relative;
					display: flex;
					justify-content: center;
					align-items: center;
					width: 100%;
					min-width: 150px;
					height: 30px;
					border-radius: 20px;
					background-color: #e6e7ed;
					cursor: pointer;

					&.moreThanFive {
						background-color: #f0f0f4;
						cursor: not-allowed;

						span {
							color: #bcbfc3;
						}
					}
					&.betweenOneAndFive {
						background-color: #f0f0f4;
						cursor: not-allowed;

						span {
							color: #bcbfc3;
						}
					}

					.description {
						position: absolute;
						display: flex;
						justify-content: flex-start;
						align-items: flex-start;
						gap: 5px;
						top: 50px;
						width: calc(100vw - 10px);
						max-width: 300px;
						padding: 10px;
						border-radius: 5px;
						background-color: #e6e7ed;
						word-break: break-all;
						color: #46474b;
						@include appleAdjustTextForSize(14, false);
						box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.1);
						z-index: 99;

						.icon {
							width: 20px;
							height: 20px;
							padding-top: 3px;
						}
					}

					span {
						@include appleAdjustTextForSize(20);
						font-weight: 400;
						color: #46474b;
					}
				}

				.email-receipt-widget {
					display: flex;
					justify-content: center;
					align-items: center;
					width: 100%;
					min-width: 150px;
					height: 30px;
					border-radius: 20px;
					background-color: #e6e7ed;
					cursor: pointer;

					span {
						@include appleAdjustTextForSize(16);
						font-weight: 400;
						color: #46474b;
					}
				}

				.payment-cancel-widget {
					position: relative;
					display: flex;
					justify-content: center;
					align-items: center;
					width: 100%;
					min-width: 150px;
					height: 30px;
					background: radial-gradient(
						circle at 50% 50%,
						var(--red),
						#bc5352 65%,
						#000 108%
					);
					border-radius: 22px;
					cursor: pointer;

					&::before {
						content: "";
						background-image: url("/img/icons/orderCancel.png");
						background-size: cover;
						background-repeat: no-repeat;
						position: absolute;
						top: 0px;
						right: 0px;
						bottom: 0px;
						left: 0px;
						opacity: 0.8;
						border-radius: 22px;
					}

					span {
						position: relative;
						@include appleAdjustTextForSize(16);
						font-weight: 400;
						color: white;
					}
				}
			}
			/* @media screen and (max-width: 1500px) {
				flex-direction: column;
				gap: 20px;
				.payment-widget {
					margin-left: auto;
				}
			} */
		}

		.transaction-wrapper {
			width: 100%;
			display: flex;
			flex-direction: column;
			gap: 10px;
		}
	}

	.not-data-container {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 10px;

		.icon {
			width: 20px;
			height: 20px;
		}
		span {
			@include appleAdjustTextForSize(16);
			font-weight: 400;
			color: #6d6f76;
			margin-bottom: 3px;
		}
	}

	.payment-add-box {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 30px;
		border: 1px solid #bcbfc3;
		border-radius: 5px;
		background-color: #e6e7ed;
		gap: 10px;
		cursor: pointer;

		span {
			@include appleAdjustTextForSize(16);
			font-weight: 400;
			color: #6d6f76;
		}

		img {
			width: 16px;
			height: 16px;
		}
	}

	@include DesktopSize {
		.card {
			padding: 15px 20px;
			background-color: white;
			border-radius: 5px;
		}

		.container {
			.payment-container {
				justify-content: space-between;
				align-items: flex-start;
				flex-direction: row;
				.payment-wrapper {
					/* gap: 60px; */
					.payment-box {
						flex-direction: row;
						justify-content: space-between;
						align-items: center;
						gap: unset;
						.img-box {
							span {
								@include appleAdjustTextForSize(16, false);
								@include heightRelativeToFontSize(20);
								font-weight: 550;
								color: #46474b;
							}
							img {
								width: 40px;
								height: 30px;
							}
						}
						.card-info {
							width: auto;
							span {
								@include appleAdjustTextForSize(14, false);
							}
						}
					}
					.amount {
						margin-left: 50px;
						margin-right: 30px;
						span {
							@include appleAdjustTextForSize(16, false);
						}
					}
				}

				.vertical-bar {
					display: block;
					width: 1px;
					height: calc(var(--divHeight));
					background-color: #d8d9df;
				}

				.payment-widget {
					display: flex;
					align-items: center;
					width: 510px;
					min-width: 510px;
					gap: 30px;
					.widget-group {
						gap: 15px;

						.top-widgets {
							flex-direction: row;
						}
						.payment-transaction-add-box {
							height: 40px;
							span {
								@include appleAdjustTextForSize(16, false);
							}
							img {
								width: 20px;
								height: 20px;
							}
						}
					}

					.cash-receipt-widget {
						height: 40px;

						.description {
							@include appleAdjustTextForSize(14, false);
							.icon {
								width: 20px;
								height: 20px;
								padding-top: 3px;
							}
						}
					}

					.email-receipt-widget {
						height: 40px;
						span {
							@include appleAdjustTextForSize(16, false);
						}
					}

					.payment-cancel-widget {
						height: 40px;
						span {
							@include appleAdjustTextForSize(16, false);
							/* font-weight: 400;
							color: white; */
						}
					}
				}
				@media screen and (max-width: 1500px) {
					flex-direction: column;
					gap: 20px;
					.payment-widget {
						margin-left: auto;
					}
				}
			}
		}

		.not-data-container {
			.icon {
				width: 24px;
				height: 24px;
			}
			span {
				@include appleAdjustTextForSize(20, false);
			}
		}
		.payment-add-box {
			height: 40px;
			span {
				@include appleAdjustTextForSize(16, false);
				@include heightRelativeToFontSize(20);
			}
			img {
				width: 20px;
				height: 20px;
			}
		}
	}
</style>
